import React from "react";

/**
 * The background-list editor's controls: undo/redo, "reset order", and — for an
 * admin — publishing the arrangement for everyone.
 *
 * Undo/redo are buttons rather than a Ctrl+Z binding on purpose (plan D5): the
 * canvas text editor owns that key, and stealing it would make typing in a
 * banner undo a background deletion.
 *
 * On a releases set page, `orderScope` adds the choice of the order that a
 * drag edits: the product's, for every set, or the set's own. The set's order
 * saves at once for everyone, so it has no history and no "Publish".
 */

export interface BgOrderScope {
  /**
   * The role can edit the product's order too (editBgList). False: only the
   * set's own order, with no "All sets" option.
   */
  canChooseAllSets?: boolean;
  /** The label of the page's set, e.g. "Splash". */
  setLabel: string;
  /** True while a drag edits the set's own order. */
  isSetScope: boolean;
  /** The set has an order of its own. */
  hasSetOrder: boolean;
  onScopeChange: (isSetScope: boolean) => void;
  /** Give the set the product's order again. */
  onClearSetOrder: () => void;
}

function OrderScopeSwitch({scope}: {scope: BgOrderScope}) {
  return (
    <div className="bg-list-controls__scope" role="radiogroup" aria-label="Background order of">
      <span className="bg-list-controls__scope-label">Order:</span>
      {scope.canChooseAllSets === false ? (
        <span
          className="bg-list-controls__scope-option bg-list-controls__scope-option--on"
          title={`Drag to give ${scope.setLabel} its own order. It saves at once for everyone.`}
        >
          {scope.setLabel}
        </span>
      ) : (
        <>
          <button
            type="button"
            role="radio"
            aria-checked={!scope.isSetScope}
            className={
              "bg-list-controls__scope-option" +
              (!scope.isSetScope ? " bg-list-controls__scope-option--on" : "")
            }
            onClick={() => scope.onScopeChange(false)}
            title="Drag to change the order of every set of the product"
          >
            All sets
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={scope.isSetScope}
            className={
              "bg-list-controls__scope-option" +
              (scope.isSetScope ? " bg-list-controls__scope-option--on" : "")
            }
            onClick={() => scope.onScopeChange(true)}
            title={`Drag to give ${scope.setLabel} its own order. It saves at once for everyone.`}
          >
            {scope.setLabel}
          </button>
        </>
      )}
      {!scope.isSetScope && scope.hasSetOrder && (
        <span className="bg-list-controls__scope-note">{scope.setLabel} has its own order</span>
      )}
    </div>
  );
}

/** The controls while a drag edits the set's own order: it saves at once. */
export function SetOrderControls({scope}: {scope: BgOrderScope}) {
  return (
    <div className="bg-list-controls bg-list-controls--set-order">
      <OrderScopeSwitch scope={scope} />
      <div className="bg-list-controls__actions">
        <span className="bg-list-controls__scope-note">Saves at once for everyone</span>
        {scope.hasSetOrder && (
          <button
            type="button"
            className="bg-list-controls__link"
            onClick={scope.onClearSetOrder}
            title={`Show ${scope.setLabel} in the order of every set again`}
          >
            Use the order of all sets
          </button>
        )}
      </div>
    </div>
  );
}

export default function BgListControls({
  canUndo,
  canRedo,
  isSaving,
  canPublish,
  onUndo,
  onRedo,
  onReset,
  onPublish,
  orderScope,
}: {
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  canPublish: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onReset: () => void;
  onPublish: () => void;
  orderScope?: BgOrderScope;
}) {
  if (orderScope?.isSetScope) return <SetOrderControls scope={orderScope} />;
  return (
    <div className={"bg-list-controls" + (orderScope ? " bg-list-controls--with-scope" : "")}>
      {orderScope && <OrderScopeSwitch scope={orderScope} />}
      <div className="bg-list-controls__history">
        <button
          type="button"
          className="bg-list-controls__btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo"
          aria-label="Undo background list change"
        >
          ↶
        </button>
        <button
          type="button"
          className="bg-list-controls__btn"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo"
          aria-label="Redo background list change"
        >
          ↷
        </button>
      </div>
      <div className="bg-list-controls__actions">
        <button
          type="button"
          className="bg-list-controls__link"
          onClick={onReset}
          disabled={isSaving}
          title="Discard your changes and return to the published order"
        >
          Reset order
        </button>
        {canPublish && (
          <button
            type="button"
            className="bg-list-controls__link bg-list-controls__link--publish"
            onClick={onPublish}
            disabled={isSaving}
            title="Make this list the one every user sees"
          >
            Publish for everyone
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The pencil that opens the background-list editor. Off by default on every
 * visit: while it is off the list shows no undo/redo, no "reset order", no
 * "publish", no drag handles and no per-tile remove/download hovers, so a
 * user who only wants to pick a background cannot delete or download one by
 * brushing past a tile. Shown only to users the editor is enabled for.
 */
export function BgEditToggle({
  isOn,
  onToggle,
  setLabel,
}: {
  isOn: boolean;
  onToggle: () => void;
  /** Only the set's order is editable: the label of the set. */
  setLabel?: string;
}) {
  return (
    <div className="bg-list-header">
      <button
        type="button"
        className={"bg-list-edit-toggle" + (isOn ? " bg-list-edit-toggle--on" : "")}
        onClick={onToggle}
        aria-pressed={isOn}
        aria-label={isOn ? "Close background editing" : "Edit backgrounds"}
        title={
          isOn
            ? "Close background editing"
            : setLabel
              ? `Order the backgrounds of ${setLabel}`
              : "Edit backgrounds: reorder, remove, download, publish"
        }
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
        </svg>
      </button>
    </div>
  );
}

/** Transient "Background removed — Undo" affordance shown after a delete. */
export function BgRemovedNotice({onUndo}: {onUndo: () => void}) {
  return (
    <div className="bg-list-removed" role="status">
      <span>Background removed</span>
      <button type="button" className="bg-list-controls__link" onClick={onUndo}>
        Undo
      </button>
    </div>
  );
}
